import {
    ArrowBack as ArrowBackIcon,
    Balance as BalanceIcon,
    Gavel as GavelIcon,
    Search as SearchIcon,
} from 'components/icons';
import AISparkleIcon from 'assets/images/icons/AISparkle.svg?react';
import { buttonStyle, ICON_SLOT } from 'components/buttons/buttonStyle';
import { focusGlobalSearch } from 'components/global-search/GlobalSearchBar';
import PageTitle from 'components/PageTitle';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

// Tokens only the new design declares, each with classic's colour as the fallback.
const CRITICAL_RED = 'var(--danger-ink, #a8071a)';
// The two translucent colours are mixed from tokens in the new design only: a mix in oklab, and a gradient through
// it, land a level off classic's literals.
const STAGE_BG = {
    new: 'radial-gradient(700px 400px at 50% 32%, color-mix(in oklab, var(--primary) 4%, transparent), transparent 60%), var(--background)',
    classic: 'radial-gradient(700px 400px at 50% 32%, rgba(51,81,49,0.04), transparent 60%), #fafafb',
};
const CRITICAL_RED_HALO = {
    new: `color-mix(in oklab, ${CRITICAL_RED} 12%, transparent)`,
    classic: 'rgba(168, 7, 26, 0.12)',
};
// The paper "evidence sticker".
const CREAM_BG = 'var(--neutral-tint, #EFEDEA)';
const EXHIBIT_BORDER = 'var(--border-menu, #d4cdc0)';
const EXHIBIT_TEXT = 'var(--foreground-secondary, #6b5a3d)';
const EXHIBIT_RING = 'var(--border-control, #b39d6e)';

const CAPTION =
    'text-fg-secondary new:text-label inline-flex items-center font-semibold uppercase new:tracking-[0.06em]';
const LINK = 'text-link inline-flex cursor-pointer items-center gap-1.5 font-medium hover:underline';

const ExhibitTag = ({ label }: { label: string }) => (
    <div
        className="new:font-mono new:shadow-card-raised new:text-caption absolute top-[6px] right-[-8px] z-2 flex rotate-[8deg] items-center gap-[7px] rounded-[3px] border py-1 pr-2 pl-2.5 font-[ui-monospace,SFMono-Regular,Menlo,monospace] text-[9.5px] font-bold tracking-[0.18em] uppercase shadow-[0_4px_10px_-6px_rgba(40,30,12,0.25)] sm:right-[-68px]"
        style={{
            backgroundColor: CREAM_BG,
            borderColor: EXHIBIT_BORDER,
            color: EXHIBIT_TEXT,
        }}
    >
        <span
            className="size-[7px] shrink-0 rounded-full border-[1.5px]"
            style={{ backgroundColor: CREAM_BG, borderColor: EXHIBIT_RING }}
        />
        {label}{' '}
        <strong className="font-extrabold" style={{ color: CRITICAL_RED }}>
            404
        </strong>
    </div>
);

const NotFoundPage = () => {
    const intl = useIntl();
    const design = useIsNewDesign() ? 'new' : 'classic';
    const title = intl.formatMessage({ id: 'not-found.title' });
    const exhibitLabel = intl.formatMessage({ id: 'not-found.exhibit' });

    return (
        <div className="flex grow items-center justify-center px-8 py-10" style={{ background: STAGE_BG[design] }}>
            <PageTitle title={title} showBreadcrumb breadcrumbContent={title} />

            <div className="relative flex w-full max-w-[600px] flex-col items-center text-center">
                <div className={`${CAPTION} mb-[22px] gap-2 text-[11px] tracking-[0.22em]`}>
                    <span
                        className="size-1.5 rounded-full"
                        style={{ backgroundColor: CRITICAL_RED, boxShadow: `0 0 0 4px ${CRITICAL_RED_HALO[design]}` }}
                    />
                    <FormattedMessage id="not-found.eyebrow" />
                </div>

                <div
                    className={`${CAPTION} before:bg-grey-300 after:bg-grey-300 mb-2.5 gap-3 text-[10px] tracking-[0.32em] before:h-px before:w-7 after:h-px after:w-7`}
                >
                    <FormattedMessage id="not-found.case-no" />
                </div>

                <div className="relative isolate inline-flex items-center">
                    <BalanceIcon
                        aria-hidden
                        className="text-primary absolute top-1/2 left-1/2 z-0 -translate-x-1/2 translate-y-[-54%] text-[260px] opacity-5"
                    />
                    <p className="text-primary new:font-mono relative z-1 font-[ui-monospace,'SF_Mono',SFMono-Regular,Menlo,Consolas,monospace] text-[128px] leading-[0.92] font-bold tracking-[-0.045em] sm:text-[184px]">
                        404
                    </p>
                    <ExhibitTag label={exhibitLabel} />
                </div>

                <div className={`${CAPTION} mt-3 mb-7 gap-2.5 text-[10.5px] tracking-[0.28em]`}>
                    <GavelIcon aria-hidden className="text-primary text-[13px] opacity-[0.6]" />
                    <FormattedMessage id="not-found.docket" />
                </div>

                <h1 className="mb-[14px] text-[28px] leading-[1.1] font-semibold tracking-[-0.012em] text-balance sm:text-[38px]">
                    <FormattedMessage
                        id="not-found.headline"
                        values={{
                            em: (chunks) => (
                                <em key="em" className="text-primary italic">
                                    {chunks}
                                </em>
                            ),
                        }}
                    />
                </h1>

                <p className="mb-2 max-w-[480px] text-[15px] leading-[1.55]">
                    <FormattedMessage id="not-found.description" />
                </p>

                <span className="text-caption text-fg-secondary mb-8 italic">
                    <FormattedMessage id="not-found.fine-print" />
                </span>

                <div className="mb-7 flex gap-2.5">
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        nativeButton={false}
                        role="link"
                        render={<Link to="/home" />}
                    >
                        <span className={ICON_SLOT.medium}>
                            <ArrowBackIcon />
                        </span>
                        <FormattedMessage id="not-found.back-to-dashboard" />
                    </Button>
                    <Button {...buttonStyle({ variant: 'outlined' })} onClick={focusGlobalSearch}>
                        <span className={ICON_SLOT.medium}>
                            <SearchIcon />
                        </span>
                        <FormattedMessage id="not-found.new-search" />
                    </Button>
                </div>

                <div className="border-divider text-fg-secondary flex w-full max-w-[460px] items-center justify-center gap-[18px] border-t pt-[22px] text-[12.5px]">
                    <Link to="/ai-chat" className={LINK}>
                        <AISparkleIcon width={14} height={14} aria-hidden />
                        <FormattedMessage id="not-found.ask-ai" />
                    </Link>
                    <span className="opacity-40">·</span>
                    <a href="mailto:support@crespect.com" className={LINK}>
                        <FormattedMessage id="not-found.contact-support" />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default NotFoundPage;
